import React, { useState } from "react";
import {
  Button,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import DeselectIcon from "@mui/icons-material/Deselect";
import { getBatchableActions, type EmailActionType } from "../actionDefinitions";

interface BatchActionBarProps {
  selectedCount: number;
  totalCount: number;
  onClose: () => void;
  onAction: (action: EmailActionType) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isExecuting: boolean;
}

/**
 * Floating action bar that appears when emails are selected
 * Features a dropdown menu for selecting batch actions
 */
export const BatchActionBar: React.FC<BatchActionBarProps> = ({
  selectedCount,
  totalCount,
  onClose,
  onAction,
  onSelectAll,
  onDeselectAll,
  isExecuting,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  if (selectedCount === 0) {
    return null;
  }

  const batchableActions = getBatchableActions();
  const allSelected = selectedCount === totalCount;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleActionSelect = (actionId: EmailActionType) => {
    handleMenuClose();
    onAction(actionId);
  };

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: "medium", minWidth: "auto" }}>
        {selectedCount} / {totalCount} selected
      </Typography>

      {/* Select All / Deselect All buttons */}
      <Tooltip title={allSelected ? "All selected" : "Select all"}>
        <span>
          <IconButton
            size="small"
            onClick={onSelectAll}
            disabled={isExecuting || allSelected}
          >
            <SelectAllIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Deselect all">
        <IconButton
          size="small"
          onClick={onDeselectAll}
          disabled={isExecuting}
        >
          <DeselectIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Actions dropdown */}
      <Button
        variant="contained"
        size="small"
        onClick={handleMenuClick}
        disabled={isExecuting}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ textTransform: "none" }}
      >
        Actions
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        {batchableActions.map((action) => {
          const IconComponent = action.icon;
          return (
            <MenuItem
              key={action.id}
              onClick={() => handleActionSelect(action.id)}
              disabled={isExecuting}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={action.label}
                secondary={action.description}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {isExecuting && <CircularProgress size={20} />}

      <IconButton size="small" onClick={onClose} disabled={isExecuting}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};
